<script lang="ts" setup>
import type { BrokerUpdatesOrderDto, TravelOrderWithTruck, TripOverview, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { API } from "@/api/api";
import type { TripUpdate } from "@/api/trips/dto/update";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import type { CheckCallDTO, CheckCallWithUpdate } from "@/api/trips/dto/check-call";
import CreateNewUpdateDialog from "@/views/trips/components/dialogs/CreateNewUpdateDialog.vue";
import ViewUpdateDialog from "@/views/trips/components/dialogs/ViewUpdateDialog.vue";
import TravelOrder from "@/views/trips/view/components/TravelOrder.vue";
import { useSnackbarStore } from "@/store/snackbar.store";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import { useTripStore } from "@/store/trip.store";
import dayjs from "dayjs";

const api = new API();

const location = useRoute();
const tripStore = useTripStore();
const { showSnackbarError, showSnackbarSuccess } = useSnackbarStore();

const props = defineProps<{
  orders: BrokerUpdatesOrderDto[];
  routes: TripRouteDto[];
}>();

const emits = defineEmits<{
  (e: "update"): void;
}>();

const checkCalls = ref<CheckCallWithUpdate[]>([]);
const showCreateUpdate = ref<boolean>(false);
const selectedTravelOrder = ref<TravelOrderWithTruck | null>(null);
const selectedCheckCall = ref<CheckCallDTO | null>(null);
const updateInfo = ref<TripUpdate | null>(null);
const showViewUpdateDialog = ref<boolean>(false);
const showIntervalDialog = ref<boolean>(false);

const currentOrder = computed(() => {
  return props.orders.find(({ id }) => id === location.params.orderId);
});

const tripId = computed(() => {
  return location.params.id.toString();
});

function openCreateUpdate(travelOrder: TravelOrderWithTruck, checkCall: CheckCallDTO) {
  showCreateUpdate.value = true;
  selectedTravelOrder.value = travelOrder;
  selectedCheckCall.value = checkCall;
}

function openIntervalDialog(travelOrder: TravelOrderWithTruck) {
  showIntervalDialog.value = true;
  selectedTravelOrder.value = travelOrder;
}

async function loadCheckCalls(): Promise<void> {
  try {
    const response = await api.trips.getCheckCalls(tripId.value);

    if (response.success) {
      checkCalls.value = response.data?.checkCalls || [];
    }
  } catch (error) {
    throw new Error("Could not load check calls");
  }
}

async function loadUpdates(): Promise<void> {
  try {
    const response = await api.trips.getUpdates(tripId.value);
    const updates = response.data?.updates || [];

    checkCalls.value = checkCalls.value.map((checkCall) => {
      const update = getUpdateForCheckCall(updates, checkCall);

      return {
        ...checkCall,
        update: update,
      };
    });
  } catch (error) {
    throw new Error("Could not load broker updates");
  }
}

function getUpdateForCheckCall(updates: TripUpdate[], checkCall: CheckCallWithUpdate): TripUpdate | undefined {
  const typesWithoutCheckingTravelOrderId = ["check-call", "dispatch-confirmed"];

  if (typesWithoutCheckingTravelOrderId.includes(checkCall.type)) {
    return updates.find((update) => update.checkCallId === checkCall.id);
  } else {
    return updates.find(
      (update) => update.checkCallId === checkCall.id && checkCall.travelOrderIds?.includes(update.travelOrderId),
    );
  }
}

async function viewUpdate(item: CheckCallWithUpdate) {
  const updateId = item.update?.id;
  const routeId = item.routeId;
  selectedCheckCall.value = item;

  if (!updateId || !routeId) {
    return;
  }

  try {
    const response = await api.events.loadBrokerUpdate(routeId, updateId);
    if (response?.data && response.success) {
      updateInfo.value = response.data;
      showViewUpdateDialog.value = true;
    }
  } catch (error) {
    showSnackbarError("Could not load update");
  }
}

async function updateInterval(interval: number | null): Promise<void> {
  if (!selectedTravelOrder.value || !selectedTravelOrder.value.id) {
    return;
  }

  try {
    const response = await api.trips.updateTravelOrderInterval(
      Number(tripId.value),
      selectedTravelOrder.value.id,
      interval,
    );

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
      emits("update");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

function getRouteByTravelOrderId(id: number | undefined) {
  return props.routes.find((route) => route.travelOrders.some((to) => to.id === id));
}

async function init() {
  await loadCheckCalls();
  await loadUpdates();
}

async function onUpdateCreation(newUpdate: TripUpdate, travelOrderId: number) {
  checkCalls.value = checkCalls.value.map((checkCall) => {
    if (checkCall.id === newUpdate.checkCallId) {
      return {
        ...checkCall,
        update: newUpdate,
      };
    }

    return checkCall;
  });

  if (!tripStore.tripOverview) {
    return;
  }

  const updatedTrip: TripOverview = {
    ...tripStore.tripOverview,
    routes: tripStore.tripOverview.routes.map((route) => {
      return {
        ...route,
        travelOrders: route.travelOrders.map((to) => {
          if (to.id === travelOrderId) {
            return {
              ...to,
              lastUpdateTime: dayjs().toString(),
            };
          }

          return to;
        }),
      };
    }),
  };

  tripStore.updateTripOverview(updatedTrip);
}

onMounted(async () => {
  await init();
});
</script>

<template>
  <div v-for="travelOrder of currentOrder?.travelOrders" :key="travelOrder.id" class="mt-11">
    <travel-order
      :check-calls="checkCalls"
      :route="getRouteByTravelOrderId(travelOrder.id)"
      :travel-order="travelOrder"
      @update-monitor="emits('update')"
      @open-create-update="openCreateUpdate"
      @view-update="viewUpdate"
      @open-interval-dialog="openIntervalDialog"
    />
  </div>

  <create-new-update-dialog
    v-if="selectedTravelOrder && selectedCheckCall && showCreateUpdate"
    v-model="showCreateUpdate"
    :check-call-id="selectedCheckCall.id"
    :check-call-type="selectedCheckCall.type"
    :route-id="selectedTravelOrder.routeId!"
    :travel-order-id="selectedTravelOrder.id!"
    @success="onUpdateCreation"
  />

  <view-update-dialog
    v-if="updateInfo && selectedCheckCall"
    v-model="showViewUpdateDialog"
    :check-call-type="selectedCheckCall?.type"
    :update="updateInfo"
  />

  <trip-interval-dialog
    v-if="selectedTravelOrder"
    v-model="showIntervalDialog"
    :interval="selectedTravelOrder.updateInterval"
    :truck-id="selectedTravelOrder.truck.id"
    :truck-number="selectedTravelOrder.truck.number"
    title="Broker update settings"
    @update-interval="updateInterval"
  />
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.bottom-row {
  height: 100px;
  background-color: $primary-light;
  border: 1px solid $grey-lighter;
}
</style>
